/**
 * Shared BNPL (Buy Now Pay Later) payment method factory.
 * Used by Klarna, Afterpay, and Affirm payment methods.
 */

import { AnalyticsService } from "../analytics";
import type { PaymentKitErrors, PaymentKitStates, TInternalFuncs } from "../types";
import {
  centeredPopupFeatures,
  definePaymentMethod,
  type FraudMetadataWire,
  getOrCreateCheckoutRequestId,
  toFraudMetadataWire,
} from "../utils";
import { type BeforeConfirmOptions, runBeforeConfirm } from "./before-confirm";
import {
  type BnplSubmitResult,
  buildClosedResult,
  buildSupersededResult,
  buildTeardownResult,
  decidePollAction,
  markBeforeHandOff,
  POPUP_CLOSED_GRACE_MS,
  SHORT_POPUP_CLOSED_GRACE_MS,
  type PollAction,
  resultAfterDetach,
  type SettleState,
  type SubmitSettler,
  type SupersedeMode,
  settleOnce,
} from "./bnpl-lifecycle";

export type BnplCustomerInfo = {
  first_name: string;
  last_name: string;
  email?: string;
};

export type BnplStartRequest = {
  processor_id: string;
  customer_info: BnplCustomerInfo;
  fraud_metadata: FraudMetadataWire;
  setup_future_usage?: boolean;
};

export type BnplStartResponse = {
  approval_url: string;
  popup_token: string;
  checkout_attempt_id: string;
};

export type BnplStatusResponse = {
  completed: boolean;
  status: "success" | "failed" | "cancelled";
  result?: unknown;
  error?: string;
};

export type BnplCheckoutResponse = {
  id: string;
  checkoutAttemptId: string;
  checkoutSessionId: string;
  state: string;
  customerId?: string;
  paymentMethodId?: string;
  processorUsed?: string;
  subscriptionId?: string;
  invoiceId?: string;
  invoiceNumber?: number;
};

export type BnplSubmitOptions = BeforeConfirmOptions & {
  processorId: string;
  customerInfo: BnplCustomerInfo;
  setupFutureUsage?: boolean;
};

type BnplStates = PaymentKitStates & {
  popup?: Window | null;
  pollInterval?: ReturnType<typeof setInterval>;
  settler?: SubmitSettler;
  activeOptions?: BnplSubmitOptions;
  startedAttemptId?: string;
  startSent?: boolean;
  /** The current submit passed its beforeConfirm and handed off to the other BNPLs. */
  handedOff?: boolean;
};

export type BnplProviderConfig = {
  name: string;
  endpointPath: string;
  supportsSetupFutureUsage?: boolean;
  popupClosedGraceMs: number;
};

export { POPUP_CLOSED_GRACE_MS };

/**
 * Describe a BNPL approval URL for telemetry WITHOUT leaking the full URL (it
 * carries a single-use authorization nonce). We record only the host and whether
 * Stripe stamped the mobile-SDK redirect params (`useWebAuthSession` /
 * `followRedirectsInSDK`) onto the redirect — the signal for diagnosing popups
 * that never leave Stripe's redirect page and "never open" the provider page.
 */
function describeApprovalUrl(approvalUrl: string): {
  approval_host: string;
  approval_has_sdk_params: boolean;
} {
  try {
    const parsed = new URL(approvalUrl);
    return {
      approval_host: parsed.host,
      approval_has_sdk_params:
        parsed.searchParams.has("useWebAuthSession") || parsed.searchParams.has("followRedirectsInSDK"),
    };
  } catch {
    return { approval_host: "", approval_has_sdk_params: false };
  }
}

const POLL_INTERVAL_MS = 2000;
// Once a reliable-close popup (Klarna, Affirm) is gone, poll this fast so the SDK settles right
// after the modal closes instead of waiting out the next 2s boundary. The backend already recorded
// the result before the popup closed, so this just reads it sooner.
const CLOSE_WATCH_INTERVAL_MS = 400;
// Stands in for a status request that got no answer. The core decides what an unread tick means.
const NO_ANSWER: BnplStatusResponse = { completed: false, status: "failed" };
// A status request that outlives two poll ticks is dropped so one stalled fetch cannot block the grace give-up.
const STATUS_FETCH_TIMEOUT_MS = POLL_INTERVAL_MS * 2;

// beforeConfirm hooks are new closures on every render, so they are left out on purpose.
const sameSubmitOptions = (a: BnplSubmitOptions | undefined, b: BnplSubmitOptions | undefined): boolean =>
  a?.processorId === b?.processorId &&
  a?.setupFutureUsage === b?.setupFutureUsage &&
  a?.customerInfo?.first_name === b?.customerInfo?.first_name &&
  a?.customerInfo?.last_name === b?.customerInfo?.last_name &&
  a?.customerInfo?.email === b?.customerInfo?.email;

const createSubmitSettler = (states: BnplStates): SubmitSettler => {
  let state: SettleState = "open";
  let detached = false;
  let resolve!: (result: BnplSubmitResult) => void;
  const promise = new Promise<BnplSubmitResult>((r) => {
    resolve = r;
  });
  const settler: SubmitSettler = {
    promise,
    settle: (result) => {
      const next = settleOnce(state);
      state = next.state;
      if (!next.accepted) return false;
      if (states.settler === settler) states.settler = undefined;
      resolve(resultAfterDetach(result, detached));
      return true;
    },
    isSettled: () => state === "settled",
    detach: () => {
      detached = true;
    },
    reattach: () => {
      detached = false;
    },
    isDetached: () => detached,
  };
  return settler;
};

/**
 * Ends the pending submit when the shopper starts another payment. Nothing locks the checkout
 * while a BNPL popup is open: the charge guard (PAY-2397) runs before every new charge, cancels
 * this payment on Stripe if it is still open, and refuses the new charge if it already succeeded.
 * A detach leaves the popup open: it fires before the card is validated or a wallet sheet is
 * confirmed, so the shopper may still finish here. If the new charge runs, the guard cancels this
 * payment and the popup fails on its own.
 */
const createSupersede =
  (states: BnplStates, name: string) =>
  (mode: SupersedeMode, closePopup = true): void => {
    const settler = states.settler;
    if (!settler || settler.isSettled()) return;
    // Before /start returns there is no payment the shopper can still approve, so a detach is an
    // abandon.
    const effective: SupersedeMode = mode === "detach" && states.startedAttemptId ? "detach" : "abandon";
    if (effective === "detach") {
      if (settler.isDetached()) return;
      settler.detach();
    } else {
      if (states.pollInterval) clearInterval(states.pollInterval);
      settler.settle(buildSupersededResult(name));
    }
    AnalyticsService.capture("bnpl_superseded", {
      provider: name,
      mode: effective,
      ...(states.startedAttemptId
        ? { checkout_attempt_id: states.startedAttemptId }
        : { start_in_flight: Boolean(states.startSent) }),
    });
    if (effective === "abandon" && closePopup && states.popup && !states.popup.closed) states.popup.close();
  };

const createSubmitPayment = (
  states: BnplStates,
  config: BnplProviderConfig,
  supersede: ReturnType<typeof createSupersede>,
) => {
  const { name, endpointPath, supportsSetupFutureUsage, popupClosedGraceMs } = config;
  const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);
  const errorResult = (message: string): BnplSubmitResult => ({ errors: { [name]: message } as PaymentKitErrors });
  const beforeHandOffResult = (errors: PaymentKitErrors): BnplSubmitResult => ({ errors: markBeforeHandOff(errors) });

  const validateOptions = (bnplOptions: BnplSubmitOptions | undefined): BnplSubmitResult | undefined => {
    if (!bnplOptions?.processorId) {
      return beforeHandOffResult({ processor_id: "Processor ID is required" });
    }
    if (!bnplOptions.customerInfo?.first_name || !bnplOptions.customerInfo?.last_name) {
      return beforeHandOffResult({ customer_name: "Customer first and last name are required" } as PaymentKitErrors);
    }
    return undefined;
  };

  const runSubmit = async (settler: SubmitSettler, options: unknown): Promise<void> => {
    const { apiBaseUrl, secureToken, environment } = states;
    const bnplOptions = options as BnplSubmitOptions;

    // Lifecycle telemetry for the BNPL popup (PostHog proj 301447).
    // AnalyticsService.capture() silently no-ops when analytics is off, so it can
    // never break checkout.
    const submittedAt = Date.now();
    const track = (event: string, props: Record<string, unknown> = {}): void =>
      AnalyticsService.capture(event, { provider: name, elapsed_ms: Date.now() - submittedAt, ...props });

    // Settle first. Side effects (telemetry, popup close) run only when this call won the settle.
    const settleThen = (result: BnplSubmitResult, afterAccepted: () => void = () => {}): boolean => {
      const accepted = settler.settle(result);
      if (accepted) afterAccepted();
      return accepted;
    };

    // Validation runs before the popup is opened so a bail doesn't leak a blank window. These are
    // pure property reads with no await, so the user activation survives them.
    const invalid = validateOptions(bnplOptions);
    if (invalid) {
      settler.settle(invalid);
      return;
    }

    // Open the popup SYNCHRONOUSLY, before any await. Browsers keep a click's transient user
    // activation alive for only ~5s, so awaiting /start first leaves window.open() unauthorised
    // on slow connections and the popup gets blocked. Open a blank window inside the activation
    // window, then navigate it once we have the approval URL.
    // Do NOT move any await above this line.
    const popup = window.open("", capitalizedName, centeredPopupFeatures());
    // A replaced submit keeps its popup for reuse by name. If the lookup missed (no window, or a
    // new one after a cross-site navigation cleared the name), close it: nothing else tracks it.
    if (states.popup && states.popup !== popup && !states.popup.closed) states.popup.close();

    if (!popup) {
      settleThen(
        beforeHandOffResult({
          [name]: `Failed to open ${capitalizedName} popup. Please allow popups for this site.`,
        } as PaymentKitErrors),
        () => track("bnpl_popup_blocked"),
      );
      return;
    }

    // Tracked immediately so cleanup() can close the blank window even if /start never resolves.
    states.popup = popup;
    track("bnpl_popup_opened");

    // Merchant pre-charge validation (PAY-1540), run AFTER the popup is open and BEFORE any
    // network call. The transient user activation was spent on window.open() above, so awaiting
    // the merchant's backend check here is free. Merchants must NOT await their validation before
    // calling submit(). Blocking before /start means no PaymentIntent exists and nothing needs
    // unwinding (/start both creates and confirms it). The timeouts are reused from the wallet
    // rails; whether BNPL needs its own budget is a question for the PAY-1540 author.
    const gate = await runBeforeConfirm(bnplOptions.beforeConfirm, bnplOptions.beforeConfirmTimeoutMs);
    if (settler.isSettled()) return;
    if (!gate.proceed) {
      settleThen(beforeHandOffResult({ [name]: gate.error } as PaymentKitErrors), () => popup.close());
      return;
    }

    // Only now end the other providers' pending submits: an invalid, blocked or gate-rejected
    // submit leaves them alone. The hand-off needs no user activation.
    states.handedOff = true;
    states._onPaymentStarting?.(name);

    const startPolling = (popupToken: string, checkoutAttemptId: string): void => {
      // `popup.closed` is NOT a reliable "customer closed it" signal. Stripe's Afterpay approval
      // URL is a 302 that sends `Cross-Origin-Opener-Policy: same-origin-allow-popups`, so the
      // handle reports `closed === true` while the customer is still paying. The status endpoint
      // is the source of truth. A closed popup only starts a per-provider grace window; Afterpay
      // keeps the long one.
      const graceMs = popupClosedGraceMs;
      let closedSince: number | undefined;
      let lastReadAt = Date.now();
      let pollInFlight = false;

      // Short-grace providers (Klarna, Affirm) have a RELIABLE `popup.closed`: the handle only
      // reports closed when the shopper actually closes the popup, which is why they get the short
      // grace. For them we add a fast close-watch below. Afterpay keeps the long grace precisely
      // because its handle reports closed while the customer still pays (COOP), so it is never
      // fast-polled and stays on the steady 2s cadence.
      const reliableClose = graceMs <= SHORT_POPUP_CLOSED_GRACE_MS;
      let closeWatch: ReturnType<typeof setInterval> | undefined;
      const stopCloseWatch = (): void => {
        if (closeWatch !== undefined) {
          clearInterval(closeWatch);
          closeWatch = undefined;
        }
      };

      const settlePoll = (result: BnplSubmitResult, afterAccepted: () => void): void => {
        settleThen(result, () => {
          clearInterval(pollInterval);
          stopCloseWatch();
          afterAccepted();
        });
      };

      const pollOnce = async (): Promise<void> => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), STATUS_FETCH_TIMEOUT_MS);
        let statusResponse: Response;
        let statusResult: BnplStatusResponse = NO_ANSWER;
        let statusRead = false;
        try {
          statusResponse = await fetch(
            `${apiBaseUrl}/api/checkout/${secureToken}/popup/status?popup_token=${popupToken}`,
            { signal: controller.signal },
          );
          if (settler.isSettled()) return;

          if (!statusResponse.ok) {
            settlePoll(errorResult(`Failed to check ${capitalizedName} status`), () => {
              track("bnpl_poll_error", { reason: "status_not_ok", status: statusResponse.status });
              popup.close();
            });
            return;
          }

          statusResult = (await statusResponse.json()) as BnplStatusResponse;
          statusRead = true;
        } catch (error) {
          if (!controller.signal.aborted) throw error;
        } finally {
          clearTimeout(timeout);
        }
        if (settler.isSettled()) return;

        const decision = decidePollAction({
          statusRead,
          lastReadAt,
          completed: statusResult.completed,
          status: statusResult.status,
          popupClosed: popup.closed,
          closedSince,
          now: Date.now(),
          graceMs,
        });
        closedSince = decision.closedSince;
        lastReadAt = decision.lastReadAt;

        const handlers: Record<PollAction, () => void> = {
          success: () =>
            settlePoll(
              {
                data: (statusResult.result as { [key: string]: unknown } | undefined) || {
                  id: checkoutAttemptId,
                  checkoutAttemptId,
                  checkoutSessionId: secureToken,
                  state: "checkout_succeeded",
                },
              },
              () => {
                popup.close();
                track("bnpl_checkout_success", { checkout_attempt_id: checkoutAttemptId });
              },
            ),
          failed: () =>
            settlePoll(errorResult(statusResult.error || `${capitalizedName} checkout ${statusResult.status}`), () => {
              popup.close();
              track("bnpl_checkout_failed", { status: statusResult.status, checkout_attempt_id: checkoutAttemptId });
            }),
          giveUp: () =>
            settlePoll(buildClosedResult(name), () => {
              popup.close();
              track("bnpl_popup_abandoned", { grace_ms: graceMs });
            }),
          wait: () => {},
        };
        handlers[decision.action]();
      };

      const tick = async (): Promise<void> => {
        if (pollInFlight) return;
        pollInFlight = true;
        try {
          await pollOnce();
        } catch (error) {
          if (settler.isSettled()) return;
          settlePoll(errorResult(`Polling error: ${error}`), () => {
            track("bnpl_poll_error", { reason: "exception" });
            popup.close();
          });
        } finally {
          pollInFlight = false;
        }
      };

      const pollInterval = setInterval(tick, POLL_INTERVAL_MS);
      states.pollInterval = pollInterval;

      if (reliableClose) {
        closeWatch = setInterval(() => {
          if (settler.isSettled()) {
            stopCloseWatch();
            return;
          }
          // Only once the popup is actually gone. `tick` is guarded by pollInFlight so this never
          // overlaps the steady poll, and the status endpoint stays the source of truth — a result
          // that is not yet terminal just keeps waiting on the existing grace.
          if (popup.closed) void tick();
        }, CLOSE_WATCH_INTERVAL_MS);
      }
    };

    const startCheckout = async (): Promise<void> => {
      const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
      const startRequestAt = Date.now();

      const requestBody: BnplStartRequest = {
        processor_id: bnplOptions.processorId,
        customer_info: bnplOptions.customerInfo,
        fraud_metadata: toFraudMetadataWire(checkoutRequestId),
      };

      if (supportsSetupFutureUsage && bnplOptions.setupFutureUsage !== undefined) {
        requestBody.setup_future_usage = bnplOptions.setupFutureUsage;
      }

      states.startSent = true;
      const startResponse = await fetch(`${apiBaseUrl}/api/checkout/${secureToken}/stripe/${endpointPath}/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-request-id": checkoutRequestId,
        },
        body: JSON.stringify(requestBody),
      });
      if (settler.isSettled()) return;

      if (!startResponse.ok) {
        let errorMessage = `Failed to start ${capitalizedName} checkout (${startResponse.status})`;
        try {
          const errorData = await startResponse.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          errorMessage = startResponse.statusText || errorMessage;
        }
        settleThen(errorResult(errorMessage), () => {
          track("bnpl_start_failed", {
            status: startResponse.status,
            start_latency_ms: Date.now() - startRequestAt,
          });
          popup.close();
        });
        return;
      }

      const { approval_url, popup_token, checkout_attempt_id } = (await startResponse.json()) as BnplStartResponse;
      if (settler.isSettled()) return;

      // A misconfigured processor can return 200 with no approval_url. Navigating the popup to an
      // empty string would leave a blank window open and poll forever, so fail loudly instead.
      if (!approval_url) {
        settleThen(errorResult(`${capitalizedName} checkout did not return an approval URL. Please try again.`), () => {
          track("bnpl_start_no_approval_url", { start_latency_ms: Date.now() - startRequestAt });
          popup.close();
        });
        return;
      }

      track("bnpl_popup_navigated", {
        checkout_attempt_id,
        start_latency_ms: Date.now() - startRequestAt,
        ...describeApprovalUrl(approval_url),
      });
      popup.location.href = approval_url;
      states.startedAttemptId = checkout_attempt_id;
      startPolling(popup_token, checkout_attempt_id);
    };

    try {
      await startCheckout();
    } catch (error) {
      if (settler.isSettled()) return;
      settleThen(errorResult(`${capitalizedName} checkout error: ${error}`), () => {
        track("bnpl_checkout_error");
        popup.close();
      });
    }
  };

  const submitPayment: TInternalFuncs["submitPayment"] = (_fields, options) => {
    // A second submit while the popup is still open brings it to the front and joins the open
    // submit: one popup, one /start, one result. The joined result is marked so only the first
    // call reaches the merchant callbacks. A detached submit is re-attached, so its errors report
    // again. Any other pending submit (popup closed or unreadable, or new options) is abandoned
    // and a new one starts.
    const pending = states.settler;
    if (pending) {
      const popupOpen = Boolean(states.popup && !states.popup.closed);
      if (popupOpen && sameSubmitOptions(states.activeOptions, options as BnplSubmitOptions)) {
        // Before the pending submit's gate passes, its own hand-off still runs after the gate.
        if (states.handedOff) states._onPaymentStarting?.(name);
        pending.reattach();
        states.popup?.focus();
        return pending.promise.then((result) => ({ ...result, joined: true as const }));
      }
      // A re-click that fails validation never reaches window.open, so it leaves the pending
      // submit and its popup alone.
      const invalid = validateOptions(options as BnplSubmitOptions);
      if (invalid) return Promise.resolve(invalid);
      // Keep the popup: the new submit reopens it by name. window.close() is async, so closing it
      // here could hand the new submit a window that is about to close.
      supersede("abandon", false);
    }
    const settler = createSubmitSettler(states);
    states.settler = settler;
    states.activeOptions = options as BnplSubmitOptions;
    states.startedAttemptId = undefined;
    states.startSent = false;
    states.handedOff = false;
    runSubmit(settler, options).catch((error) => {
      settler.settle(errorResult(`${capitalizedName} checkout error: ${error}`));
    });
    return settler.promise;
  };

  return submitPayment;
};

export function createBnplPaymentMethod(config: BnplProviderConfig) {
  return definePaymentMethod((paymentKitStates) => {
    const localStates: BnplStates = { ...paymentKitStates };
    const supersede = createSupersede(localStates, config.name);

    return {
      name: config.name,
      externalFuncs: {},
      internalFuncs: {
        submitPayment: createSubmitPayment(localStates, config, supersede),
        supersede,
        cleanup: () => {
          if (localStates.pollInterval) {
            clearInterval(localStates.pollInterval);
          }
          const accepted = localStates.settler?.settle(buildTeardownResult(config.name));
          if (accepted) {
            AnalyticsService.capture("bnpl_teardown_pending", {
              provider: config.name,
              ...(localStates.startedAttemptId
                ? { checkout_attempt_id: localStates.startedAttemptId }
                : { start_in_flight: Boolean(localStates.startSent) }),
            });
          }
          if (accepted && localStates.popup && !localStates.popup.closed) {
            localStates.popup.close();
          }
        },
      },
    };
  });
}
